import { useApi } from '@backstage/core-plugin-api';
import { uptimeMonitorApiRef } from '../api/UptimeMonitorApi';
import { useCallback, useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { IssuesRequest, IssuesListResponse } from '../types';

export const useIssues = (params: IssuesRequest & { projectId?: string }) => {
  const api = useApi(uptimeMonitorApiRef);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  // Default query parameters
  const [queryParams, setQueryParams] = useState<Record<string, string>>({
    sort: '-last_seen',
  });

  const getIssues = useCallback(async (): Promise<IssuesListResponse> => {
    try {
      setTimeout(() => (document.activeElement as HTMLElement).blur(), 0);

      // Include cursor, projectId, and other query parameters
      const currentParams = {
        ...params,
        query: {
          ...queryParams,
          cursor: cursor,
          ...(params.projectId && { projectId: params.projectId }),
        },
      };

      const result = await api.getIssues(currentParams);
      return result;
    } catch (err: any) {
      console.error('Error fetching issues, returning empty data:', err);
      // Return proper empty structure matching IssuesListResponse
      return {
        issues: [],
        pagination: {
          next: undefined,
          previous: undefined,
        },
      };
    }
  }, [api, params, cursor, queryParams]);

  const [state, fetch] = useAsyncFn(
    () => getIssues(),
    [params.host, params.org, params.projectId, cursor, queryParams],
  );

  useEffect(() => {
    fetch();
  }, [params.host, params.org, params.projectId, cursor, queryParams, fetch]);

  const handlePageChange = useCallback((newCursor: string | undefined) => {
    setCursor(newCursor);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: Record<string, string>) => {
      setQueryParams(prev => ({ ...prev, ...newFilters }));
      setCursor(undefined);
    },
    [],
  );

  const handleSearch = useCallback((searchQuery: string) => {
    setQueryParams(prev => ({ ...prev, query: searchQuery }));
    setCursor(undefined);
  }, []);

  const refetch = useCallback(() => {
    fetch();
  }, [fetch]);

  const currentPageSize = parseInt(queryParams.limit || '10');
  const hasNextPage = !!state?.value?.pagination?.next;
  const hasPreviousPage = !!state?.value?.pagination?.previous;
  const currentItemCount = state?.value?.issues?.length || 0;

  return {
    isLoading: state.loading,
    data: state?.value?.issues || [],
    pagination: {
      ...state?.value?.pagination,
      hasNext: hasNextPage,
      hasPrevious: hasPreviousPage,
      currentPageSize,
      currentItemCount,
    },
    error: undefined,
    handlePageChange,
    handleFilterChange,
    handleSearch,
    refetch,
    currentFilters: queryParams,
  };
};